import React, { useEffect, useState } from 'react';
import { InternalFieldName, useForm } from 'react-hook-form';
import { FaAddressBook, FaEdit, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export type PersonAdressModel = {
  id: number;
  address: string;
  number?: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  postalCode: string;
};

export interface PersonAdressFormFields extends PersonAdressModel {}

interface PersonAdressProps {
  itens: PersonAdressModel[];
  onSubmitAddress: (data: PersonAdressFormFields) => Promise<void>;
  removeAddress: (address: PersonAdressModel) => Promise<void>;
  editAddress: (addressId: number) => Promise<void>;
  formErrors: Record<string, any>;
  formFields: PersonAdressFormFields;
  isUpdateLoading: boolean;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export function PersonAddresses({
  itens,
  onSubmitAddress,
  removeAddress,
  editAddress,
  formErrors,
  formFields,
  isUpdateLoading,
}: PersonAdressProps) {
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isValid },
  } = useForm<PersonAdressFormFields>({ mode: 'onBlur' });

  const [isUpdate, setIsUpdate] = useState(false);
  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  const resetModal = (fields?: PersonAdressFormFields, isNew = true) => {
    setIsUpdate(!isNew);
    reset({
      id: fields?.id || undefined,
      address: fields?.address || '',
      city: fields?.city || '',
      complement: fields?.complement || '',
      neighborhood: fields?.neighborhood || '',
      number: fields?.number || '',
      postalCode: fields?.postalCode || '',
      state: fields?.state || '',
    });
  };

  useEffect(() => {
    resetModal(formFields, false);
  }, [formFields]);

  return (
    <>
      <div className='table-responsive'>
        <table className='table table-hover'>
          <thead>
            <tr>
              <th>Endereço</th>
              <th className='text-end'>Número</th>
              <th>Complemento</th>
              <th>Bairro</th>
              <th>Cidade</th>
              <th>Estado</th>
              <th>CEP</th>
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {itens.length > 0 ? (
              itens.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Link
                      className='nav-link active p-0 text-black'
                      to='/'
                      role='button'
                      data-bs-toggle='modal'
                      data-bs-target='#person-address-modal'
                      onClick={() => editAddress(item.id)}
                    >
                      {item.address}
                    </Link>
                  </td>
                  <td className='text-end'>
                    {item.number ? item.number : 'S/N'}
                  </td>
                  <td>{item.complement}</td>
                  <td>{item.neighborhood}</td>
                  <td>{item.city}</td>
                  <td>{item.state}</td>
                  <td>{item.postalCode}</td>
                  <td>
                    <button
                      className='btn btn-sm btn-danger'
                      type='button'
                      onClick={() => removeAddress(item)}
                    >
                      <FaTimes />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8}>Nenhum registro</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-end mt-2'>
        <button
          type='button'
          className='btn btn-secondary btn-sm'
          data-bs-toggle='modal'
          data-bs-target='#person-address-modal'
          onClick={() => resetModal()}
        >
          Adicionar endereço <FaAddressBook />
        </button>

        {/* Modal */}
        <div
          className='modal'
          tabIndex={-1}
          id='person-address-modal'
          data-bs-backdrop='static'
          data-bs-keyboard='false'
          aria-labelledby='staticBackdropLabel'
          aria-hidden='true'
        >
          <div className='modal-dialog modal-xl'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>
                  {isUpdate ? 'Alterar' : 'Adicionar'} endereço
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  data-bs-dismiss='modal'
                  aria-label='Close'
                />
              </div>
              <div className='modal-body text-start'>
                {isUpdateLoading ? (
                  <div className='d-flex justify-content-center'>
                    <div className='spinner-border' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmitAddress)}>
                    <div className='row'>
                      <div className='col-md-9'>
                        <div className='mb-3'>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor='inputLabel' className='form-label'>
                            Endereço
                          </label>
                          <input
                            {...register('address', { required: true })}
                            className='form-control'
                            placeholder='Endereço'
                            required
                          />
                          {errors.address && (
                            <div className='form-text text-danger'>
                              {errors.address.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='col-md-3'>
                        <div className='mb-3'>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor='inputLabel' className='form-label'>
                            Número
                          </label>
                          <input
                            {...register('number')}
                            className='form-control'
                            placeholder='Número'
                            type='number'
                          />
                          {errors.number && (
                            <div className='form-text text-danger'>
                              {errors.number.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col'>
                        <div className='mb-3'>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor='inputLabel' className='form-label'>
                            Complemento
                          </label>
                          <input
                            {...register('complement')}
                            className='form-control'
                            placeholder='Complemento'
                          />
                          {errors.complement && (
                            <div className='form-text text-danger'>
                              {errors.complement.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='col'>
                        <div className='mb-3'>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor='inputLabel' className='form-label'>
                            Bairro
                          </label>
                          <input
                            {...register('neighborhood')}
                            className='form-control'
                            placeholder='Bairro'
                          />
                          {errors.neighborhood && (
                            <div className='form-text text-danger'>
                              {errors.neighborhood.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='row'>
                      <div className='col'>
                        <div className='mb-3'>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor='inputLabel' className='form-label'>
                            Cidade
                          </label>
                          <input
                            {...register('city')}
                            className='form-control'
                            placeholder='Cidade'
                          />
                          {errors.city && (
                            <div className='form-text text-danger'>
                              {errors.city.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='col-md-3'>
                        <div className='mb-3'>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor='inputLabel' className='form-label'>
                            Estado
                          </label>
                          <input
                            {...register('state')}
                            className='form-control'
                            placeholder='Estado'
                          />
                          {errors.state && (
                            <div className='form-text text-danger'>
                              {errors.state.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='row'>
                      <div className='col-md-3'>
                        <div className='mb-3'>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor='inputLabel' className='form-label'>
                            CEP
                          </label>
                          <input
                            {...register('postalCode')}
                            className='form-control'
                            placeholder='CEP'
                            type='tel'
                            pattern='[0-9]{8}$'
                          />
                          {errors.postalCode && (
                            <div className='form-text text-danger'>
                              {errors.postalCode.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='text-end mt-3'>
                      <button type='submit' className='btn btn-primary'>
                        Salvar
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
